import { AlertTriangle, CalendarClock, PauseCircle } from 'lucide-react'
import {
  resumenDelPlazo,
  type PlazoDelExpediente as Plazo,
  type TonoDelPlazo,
  type VistaDelPlazo,
} from '@/features/proveedores/lib/plazoDelExpediente'

// CC-02 · SP-248. Plazo para completar el expediente. El proveedor ve el suyo y
// lo que le falta; Compras y Contraloría ven el del proveedor, incluido si ya
// venció y lo tiene bloqueado.

const ESTILOS: Record<TonoDelPlazo, { caja: string; icono: string; faltantes: string }> = {
  azul: {
    caja: 'bg-blue-50 border-blue-200 text-blue-900',
    icono: 'text-blue-600',
    faltantes: 'text-blue-800',
  },
  amarillo: {
    caja: 'bg-yellow-50 border-yellow-200 text-yellow-900',
    icono: 'text-yellow-600',
    faltantes: 'text-yellow-800',
  },
  rojo: {
    caja: 'bg-red-50 border-red-200 text-red-900',
    icono: 'text-red-600',
    faltantes: 'text-red-800',
  },
}

interface PlazoDelExpedienteProps {
  plazo: Plazo | null | undefined
  vista: VistaDelPlazo
  className?: string
}

const Lista = ({ titulo, items, clase }: { titulo: string; items: string[]; clase: string }) => (
  <div>
    <p className={`text-xs font-semibold uppercase tracking-wide ${clase}`}>{titulo}</p>
    <ul className="mt-1 list-disc pl-5 space-y-0.5 text-sm">
      {items.map((texto) => (
        <li key={texto}>{texto}</li>
      ))}
    </ul>
  </div>
)

const PlazoDelExpediente = ({ plazo, vista, className = '' }: PlazoDelExpedienteProps) => {
  const resumen = resumenDelPlazo(plazo, vista)
  if (!resumen) return null

  const estilo = ESTILOS[resumen.tono]
  const Icono =
    resumen.tono === 'rojo' ? AlertTriangle : plazo?.state === 'paused' ? PauseCircle : CalendarClock
  const hayFaltantes = resumen.datosFaltantes.length > 0 || resumen.documentosFaltantes.length > 0

  return (
    <div
      role="status"
      data-testid="plazo-del-expediente"
      data-estado={plazo?.state ?? undefined}
      className={`p-4 border rounded-lg ${estilo.caja} ${className}`}
    >
      <div className="flex items-start gap-3">
        <Icono className={`w-5 h-5 mt-0.5 flex-shrink-0 ${estilo.icono}`} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{resumen.titulo}</p>
          {resumen.detalle && <p className="text-sm mt-0.5">{resumen.detalle}</p>}

          {hayFaltantes && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {resumen.datosFaltantes.length > 0 && (
                <Lista
                  titulo="Datos que faltan"
                  items={resumen.datosFaltantes}
                  clase={estilo.faltantes}
                />
              )}
              {resumen.documentosFaltantes.length > 0 && (
                <Lista
                  titulo="Documentos que faltan"
                  items={resumen.documentosFaltantes}
                  clase={estilo.faltantes}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PlazoDelExpediente
